import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X, AudioLines, ArrowRight, Info } from 'lucide-react';
// FONTE ÚNICA dos planos: landing/src/data/pricingPlans.json. Este arquivo é um
// SNAPSHOT sincronizado automaticamente pelo scripts/sync-pricing.mjs em todo
// npm start/build (a landing é um repo irmão; na VPS o snapshot commitado vale).
// Preço, link, limite ou feature: editar SÓ o JSON da landing. Os textos
// visíveis usam as MESMAS chaves i18n pricing.* (espelhadas em pt/en/es do app).
import pricingData from '../../data/pricingPlans.json';

const SUPPORT_PHONE = '5577988744698';

// Ordem dos tiers p/ decidir upgrade x downgrade (quarterly = família pro).
const FAMILY_RANK = { free: 0, basic: 1, lite: 2, pro: 3 };
// plan_type efetivo do backend → família de card.
const PLAN_TO_FAMILY = { free: 'free', basic: 'basic', lite: 'lite', pro: 'pro', plus: 'pro' };

const FEATURE_LABEL_KEYS = pricingData.featureKeys.map((k) => `pricing.features.${k}`);

/* Mensagem de upgrade pro WhatsApp do suporte (sempre PT — suporte é BR). */
const buildUpgradeLink = (planName, email) => {
  const msg = `Olá! Gostaria de fazer upgrade do meu plano para o ${planName}. Meu e-mail é: ${email || ''}`;
  return `https://wa.me/${SUPPORT_PHONE}?text=${encodeURIComponent(msg)}`;
};

function formatBRL(value, decimals) {
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/* Preço animado (count-up suave) — portado 1:1 da landing. */
function AnimatedPrice({ value }) {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  const rafRef = useRef(0);

  useEffect(() => {
    const from = fromRef.current;
    const to = value;
    if (from === to) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setDisplay(to);
      fromRef.current = to;
      return;
    }

    const duration = 680;
    let startTs = 0;
    const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    const tick = (now) => {
      if (!startTs) startTs = now;
      const t = Math.min(1, (now - startTs) / duration);
      setDisplay(from + (to - from) * ease(t));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = to;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value]);

  const decimals = Number.isInteger(value) ? 0 : 2;
  return <>{formatBRL(display, decimals)}</>;
}

/* Toggle Mensal/Trimestral — portado 1:1 da landing. */
function PeriodToggle({ period, onChange }) {
  const { t } = useTranslation();
  const isYearly = period === 'yearly';
  return (
    <div className="pricing-toggle">
      <button
        type="button"
        className={`pricing-toggle-label ${isYearly ? '' : 'is-active'}`}
        onClick={() => onChange('monthly')}
      >
        {t('pricing.toggle.monthly')}
      </button>
      <button
        type="button"
        role="switch"
        aria-checked={isYearly}
        aria-label={t('pricing.toggle.ariaLabel')}
        className={`pricing-switch ${isYearly ? 'is-on' : ''}`}
        onClick={() => onChange(isYearly ? 'monthly' : 'yearly')}
      >
        <span className="pricing-switch-knob" aria-hidden="true" />
      </button>
      <button
        type="button"
        className={`pricing-toggle-label ${isYearly ? 'is-active' : ''}`}
        onClick={() => onChange('yearly')}
      >
        {t('pricing.toggle.yearly')}
      </button>
      <span className="pricing-save-badge">{t('pricing.toggle.saveBadge')}</span>
    </div>
  );
}

/* Card — markup/estilos EXATOS da landing; só o CTA muda conforme o usuário. */
function PricingCard({ plan, period, cta }) {
  const { t, i18n } = useTranslation();
  const price = plan.fixedTotal ? plan.fixedTotal.perMonth : plan.price[period];
  const billed = plan.free
    ? t('pricing.billed.noCard')
    : plan.fixedTotal
      ? plan.fixedTotal.totalLabel
      : period === 'yearly'
        ? t('pricing.billed.yearly', {
            total: formatBRL(plan.yearlyTotal ?? plan.price.yearly * 3, 2),
          })
        : t('pricing.billed.monthly');

  const credits = plan.audioTip.audios * plan.audioTip.chars * 1000;
  const creditsStr = new Intl.NumberFormat(i18n.language || 'pt-BR', {
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: 1,
  }).format(credits);
  const creditsLabel = t(
    plan.free ? 'pricing.credits.total' : 'pricing.credits.perDay',
    { credits: creditsStr }
  );
  const audioTipText = t(
    plan.free ? 'pricing.audioTip.free' : 'pricing.audioTip.daily',
    { credits: creditsStr, audios: plan.audioTip.audios, chars: plan.audioTip.chars }
  );

  return (
    <div className={`h-full-pricing ${plan.popular ? 'pricing-item-popular' : ''}`}>
      <div className={`pricing-card ${plan.popular ? 'is-popular' : ''}`}>
        {plan.popular && <div className="pricing-ribbon">{t('pricing.ribbon')}</div>}

        {plan.fixedTotal?.discountTag && (
          <div className="pricing-discount-tag">{plan.fixedTotal.discountTag}</div>
        )}

        <div className="pricing-card-inner">
          <h3 className="pricing-name">
            {plan.name}
            {plan.proTag && (
              <span className="pricing-pro-tag" tabIndex={0} aria-label={plan.proTag.tip}>
                {plan.proTag.label}
                <span className="pricing-pro-tip" role="tooltip">{plan.proTag.tip}</span>
              </span>
            )}
          </h3>

          {plan.free ? (
            <div className="pricing-price-row">
              <span className="pricing-amount">{t('pricing.price.free')}</span>
            </div>
          ) : (
            <div className="pricing-price-row">
              <span className="pricing-currency">R$</span>
              <span className="pricing-amount">
                <AnimatedPrice value={price} />
              </span>
              <span className="pricing-period">{t('pricing.price.perMonth')}</span>
            </div>
          )}
          <p className="pricing-billed">{billed}</p>

          <div className="pricing-audio">
            <AudioLines size={16} className="pricing-audio-icon" />
            <span className="pricing-audio-text">
              <span className="pricing-audio-main">
                {plan.audio}
                {plan.audioNote && (
                  <span className="pricing-audio-note"> {plan.audioNote}</span>
                )}
              </span>
              <span className="pricing-audio-credits">{creditsLabel}</span>
            </span>
            <span
              className="pricing-audio-info"
              tabIndex={0}
              role="button"
              aria-label={audioTipText}
            >
              <Info size={12} strokeWidth={2.5} />
              <span className="pricing-audio-tip" role="tooltip">
                {audioTipText}
              </span>
            </span>
          </div>

          {/* CTA — varia conforme o plano atual do usuário (current/upgrade/checkout). */}
          {cta}

          <div className="pricing-features-panel">
            <ul className="pricing-features">
              {FEATURE_LABEL_KEYS.map((labelKey, i) => {
                const on = plan.included[i];
                return (
                  <li
                    key={labelKey}
                    className={`pricing-feature ${on ? '' : 'is-excluded'}`}
                  >
                    <span className="pricing-feature-icon">
                      {on ? (
                        <Check size={11} strokeWidth={3} />
                      ) : (
                        <X size={11} strokeWidth={3} />
                      )}
                    </span>
                    <span>{t(labelKey)}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Grade de planos da página Meu Plano — os MESMOS cards da landing.
 * Regras de CTA:
 *  - card do plano atual          → "Plano atual" (desabilitado, com check)
 *  - plano MAIOR (pagante)        → "Atualizar" → WhatsApp do suporte (upgrade)
 *  - plano MENOR (pagante)        → desabilitado (downgrade = cancelar + assinar; ver FAQ)
 *  - usuário free / sem plano     → CTA original da landing (checkout Cakto)
 */
const PlanCardsSection = ({ subscriptionData, isSuperAdmin }) => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState('monthly');

  const planType = subscriptionData?.plan_type?.toLowerCase() || 'free';
  const email = subscriptionData?.email || '';
  const isTrial = planType === 'trial';
  const hasPaidPlan = !!subscriptionData?.has_subscription && !isTrial && !isSuperAdmin;
  const currentFamily = isSuperAdmin
    ? null
    : hasPaidPlan
      ? (PLAN_TO_FAMILY[planType] || 'pro')
      : 'free';
  const currentRank = currentFamily != null ? (FAMILY_RANK[currentFamily] ?? 0) : null;

  const plans = useMemo(() => pricingData.plans.map((p) => ({
    id: p.id,
    planFamily: p.planFamily,
    name: p.name || t(`pricing.plans.${p.id}.name`),
    audio: t(`pricing.plans.${p.id}.audio`),
    audioNote: p.hasAudioNote ? t(`pricing.plans.${p.id}.audioNote`) : undefined,
    audioTip: p.audioTip,
    popular: p.popular || undefined,
    proTag: p.proTag
      ? { label: t(`pricing.plans.${p.id}.proTagLabel`), tip: t(`pricing.plans.${p.id}.proTagTip`) }
      : undefined,
    free: p.free || undefined,
    cta: t(`pricing.plans.${p.id}.cta`),
    price: p.price,
    yearlyTotal: p.yearlyTotal ?? undefined,
    link: p.link,
    included: p.included,
    fixedTotal: p.fixedTotal
      ? {
          perMonth: p.fixedTotal.perMonth,
          total: p.fixedTotal.total,
          totalLabel: t(`pricing.plans.${p.id}.totalLabel`),
          discountTag: t(`pricing.plans.${p.id}.discountTag`),
        }
      : undefined,
  })), [t]);

  // O card "Trimestral" NÃO aparece no app (pedido do owner): com o toggle em
  // Trimestral o card Pro já exibe a MESMA oferta (R$ 99,99/mês, total 299,99,
  // mesmo checkout 3tr7n9k) — seria o PRO duas vezes. Grade fixa em 4 cards.
  const visiblePlans = useMemo(
    () => plans.filter((p) => p.id !== 'quarterly'),
    [plans]
  );

  // Só o PRIMEIRO card da família atual ganha "Plano atual" (pro mensal antes do trimestral).
  const currentIndex = currentFamily != null
    ? visiblePlans.findIndex((p) => p.planFamily === currentFamily)
    : -1;

  const renderCta = (plan, index) => {
    const baseClass = plan.popular ? 'pricing-cta is-primary' : 'pricing-cta';

    if (index === currentIndex && (hasPaidPlan || currentFamily === 'free')) {
      return (
        <button type="button" className="pricing-cta is-current" disabled>
          <Check size={15} strokeWidth={2.5} />
          {t('pricing.app.cta_current')}
        </button>
      );
    }

    if (hasPaidPlan) {
      const rank = FAMILY_RANK[plan.planFamily] ?? 0;
      if (rank < currentRank) {
        return (
          <button
            type="button"
            className="pricing-cta is-disabled"
            disabled
            title={t('pricing.app.lower_hint')}
          >
            {t('pricing.app.cta_lower')}
          </button>
        );
      }
      return (
        <a
          href={buildUpgradeLink(plan.name, email)}
          target="_blank"
          rel="noopener noreferrer"
          className={baseClass}
        >
          {t('pricing.app.cta_upgrade')}
          <ArrowRight size={15} strokeWidth={2.5} />
        </a>
      );
    }

    // Free / sem plano / admin: comportamento original da landing (checkout).
    return (
      <a
        href={plan.link[period]}
        target="_blank"
        rel="noopener noreferrer"
        className={baseClass}
      >
        {plan.cta}
        <ArrowRight size={15} strokeWidth={2.5} />
      </a>
    );
  };

  return (
    <div style={{ marginBottom: '24px' }}>
      <div style={{ textAlign: 'center', margin: '4px 0 22px' }}>
        <PeriodToggle period={period} onChange={setPeriod} />
      </div>

      <div className={`pricing-grid ${visiblePlans.length === 4 ? 'cols-4' : ''}`}>
        {visiblePlans.map((plan, i) => (
          <PricingCard key={plan.id} plan={plan} period={period} cta={renderCta(plan, i)} />
        ))}
      </div>

      {/* Estilos EXATOS da landing (Pricing.tsx) + estados de CTA do app
          (is-current / is-disabled). Não alterar aqui sem alterar lá. */}
      <style>{`
        .h-full-pricing { height: 100%; }

        /* ─── GRID (5 cols; 4 no Mensal, sem o card Trimestral) ─── */
        .pricing-grid {
          display: grid; grid-template-columns: repeat(5, 1fr);
          gap: 14px; align-items: center;
          max-width: 1320px; margin: 0 auto;
        }
        /* :where() zera a especificidade extra → as media queries abaixo
           continuam vencendo nos breakpoints. */
        .pricing-grid:where(.cols-4) {
          grid-template-columns: repeat(4, 1fr);
          max-width: 1100px;
        }

        /* ─── CARD ─── */
        .pricing-card {
          position: relative; height: 100%;
          display: flex; flex-direction: column;
          border-radius: 20px;
          background: #141414;
          border: 1px solid rgba(255,255,255,0.06);
          transition: border-color 350ms cubic-bezier(0.16,1,0.3,1),
            transform 350ms cubic-bezier(0.16,1,0.3,1);
        }
        .pricing-card:hover {
          border-color: rgba(13,174,255,0.15);
          transform: translateY(-2px);
        }
        .pricing-card-inner {
          display: flex; flex-direction: column; flex: 1;
          padding: 18px 16px;
        }

        .pricing-card.is-popular {
          background: linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%);
          border-color: transparent;
          transform: scale(1.05); z-index: 2;
        }
        .pricing-card.is-popular:hover {
          border-color: transparent;
          transform: scale(1.05) translateY(-2px);
        }
        .is-popular .pricing-card-inner {
          background: #141414; border-radius: 16px;
          margin: 0 3px 3px; padding: 18px 16px;
        }
        .pricing-ribbon {
          display: flex; align-items: center; justify-content: center;
          height: 32px; color: #fff;
          font-size: 13px; font-weight: 700; letter-spacing: 0.12em;
        }

        /* ─── HEAD ─── */
        .pricing-name {
          font-size: 16px; font-weight: 700; color: #fff; margin: 0 0 8px;
          line-height: 1.25; letter-spacing: -0.2px;
          display: flex; align-items: center; flex-wrap: wrap; gap: 2px 0;
        }

        /* ─── PREÇO ─── */
        .pricing-price-row { display: flex; align-items: baseline; gap: 3px; }
        .pricing-currency { font-size: 14px; font-weight: 600; color: rgba(255,255,255,0.6); }
        .pricing-amount {
          font-family: var(--dp-font-display, var(--dp-font, 'Outfit', sans-serif));
          font-size: 30px; font-weight: 800; line-height: 1; color: #fff;
          font-variant-numeric: tabular-nums; letter-spacing: -1.2px;
        }
        .pricing-period { font-size: 12.5px; color: rgba(255,255,255,0.45); }
        .pricing-billed {
          font-size: 11px; color: rgba(255,255,255,0.4); margin: 5px 0 12px;
        }

        /* ─── ÁUDIO ─── */
        .pricing-audio {
          position: relative;
          display: flex; align-items: flex-start; gap: 7px;
          padding: 8px 22px 8px 10px; border-radius: 9px; margin-bottom: 13px;
          background: rgba(13,174,255,0.05);
          border: 1px solid rgba(13,174,255,0.15);
          font-size: 12px; line-height: 1.35; color: rgba(255,255,255,0.8);
        }
        .pricing-audio-icon { color: #0DAEFF; flex-shrink: 0; margin-top: 1px; }
        .pricing-audio-note {
          font-size: 10.5px; color: rgba(255,255,255,0.5);
        }
        .pricing-audio-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
        .pricing-audio-credits {
          font-size: 11.5px; font-weight: 800; color: #0DAEFF;
          letter-spacing: -0.1px; font-variant-numeric: tabular-nums;
        }

        /* ─── INFO ⓘ + TOOLTIP ─── */
        .pricing-audio-info {
          position: absolute; top: 6px; right: 7px;
          display: inline-flex; align-items: center; justify-content: center;
          color: rgba(255,255,255,0.3);
          cursor: help; outline: none;
          transition: color 160ms ease;
        }
        .pricing-audio-info:hover, .pricing-audio-info:focus-visible {
          color: rgba(13,174,255,0.95);
        }
        .pricing-audio-tip {
          position: absolute; bottom: calc(100% + 8px); right: -6px;
          width: max-content; max-width: 230px;
          padding: 9px 11px; border-radius: 9px;
          background: #1C1C1C; border: 1px solid rgba(13,174,255,0.25);
          color: rgba(255,255,255,0.92); font-size: 11.5px; font-weight: 500;
          line-height: 1.5; letter-spacing: 0; text-align: left;
          box-shadow: 0 12px 32px -8px rgba(0,0,0,0.75);
          opacity: 0; pointer-events: none;
          transform: translateY(4px);
          transition: opacity 180ms ease, transform 180ms ease;
          z-index: 20;
        }
        .pricing-audio-tip::after {
          content: ""; position: absolute; top: 100%; right: 10px;
          border: 5px solid transparent; border-top-color: #1C1C1C;
        }
        .pricing-audio-info:hover .pricing-audio-tip,
        .pricing-audio-info:focus .pricing-audio-tip {
          opacity: 1; transform: translateY(0);
        }

        /* ─── CTA ─── */
        .pricing-cta {
          display: inline-flex; align-items: center; justify-content: center; gap: 8px;
          width: 100%; height: 40px; border-radius: 10px;
          font-size: 13.5px; font-weight: 600; text-decoration: none;
          color: #fff; cursor: pointer;
          background: rgba(13,174,255,0.1);
          border: 1px solid rgba(13,174,255,0.3);
          transition: all 0.2s ease;
        }
        .pricing-cta:hover {
          background: rgba(13,174,255,0.16); transform: translateY(-1px);
        }
        .pricing-cta.is-primary {
          background: linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%);
          border-color: transparent;
          box-shadow: 0 6px 20px -6px rgba(13,174,255,0.45);
        }
        .pricing-cta.is-primary:hover {
          background: linear-gradient(135deg, #1FB8FF 0%, #4F8FF8 100%);
        }
        .pricing-cta svg {
          transition: transform 200ms cubic-bezier(0.4, 0, 0.2, 1);
        }
        .pricing-cta:hover svg {
          transform: rotate(-45deg);
        }

        /* CTA do app: plano ATUAL (check verde, sem ação) */
        .pricing-cta.is-current {
          background: rgba(52,211,153,0.08);
          border: 1px solid rgba(52,211,153,0.30);
          color: #34D399; cursor: default;
        }
        .pricing-cta.is-current:hover { transform: none; background: rgba(52,211,153,0.08); }
        .pricing-cta.is-current svg { transition: none; }
        .pricing-cta.is-current:hover svg { transform: none; }

        /* CTA do app: plano MENOR (downgrade indisponível; ver FAQ) */
        .pricing-cta.is-disabled {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.10);
          color: rgba(255,255,255,0.40); cursor: not-allowed;
        }
        .pricing-cta.is-disabled:hover { transform: none; background: rgba(255,255,255,0.04); }

        /* ─── TAG "PRO" (Trimestral) + TOOLTIP ─── */
        .pricing-pro-tag {
          position: relative; display: inline-flex; align-items: center;
          margin-left: 8px; padding: 2px 8px; border-radius: 999px;
          font-size: 10.5px; font-weight: 800; letter-spacing: 0.04em; color: #fff;
          vertical-align: middle; cursor: help; outline: none;
          background: linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%);
        }
        .pricing-pro-tip {
          position: absolute; bottom: calc(100% + 9px); left: 50%;
          transform: translateX(-50%) translateY(4px);
          width: max-content; max-width: 200px;
          padding: 8px 11px; border-radius: 9px;
          background: #1C1C1C; border: 1px solid rgba(13,174,255,0.25);
          color: rgba(255,255,255,0.9); font-size: 11.5px; font-weight: 500;
          line-height: 1.35; letter-spacing: 0; text-align: center;
          box-shadow: 0 10px 30px -8px rgba(0,0,0,0.7);
          opacity: 0; pointer-events: none;
          transition: opacity 180ms ease, transform 180ms ease;
          z-index: 10;
        }
        .pricing-pro-tip::after {
          content: ""; position: absolute; top: 100%; left: 50%;
          transform: translateX(-50%);
          border: 5px solid transparent; border-top-color: #1C1C1C;
        }
        .pricing-pro-tag:hover .pricing-pro-tip,
        .pricing-pro-tag:focus .pricing-pro-tip {
          opacity: 1; transform: translateX(-50%) translateY(0);
        }

        /* ─── FEATURES ✓/✕ ─── */
        .pricing-features-panel {
          margin-top: 14px; flex: 1;
          padding: 14px 13px; border-radius: 14px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.07);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.05),
            0 6px 20px -12px rgba(0,0,0,0.55);
        }
        .is-popular .pricing-features-panel {
          background: rgba(255,255,255,0.05);
          border-color: rgba(255,255,255,0.08);
        }
        .pricing-features {
          list-style: none; margin: 0; padding: 0;
          display: flex; flex-direction: column; gap: 9px;
        }
        .pricing-feature {
          display: flex; align-items: flex-start; gap: 9px;
          font-size: 12.5px; line-height: 1.35; color: rgba(255,255,255,0.85);
        }
        .pricing-feature-icon {
          flex-shrink: 0; width: 15px; height: 15px; border-radius: 50%;
          display: inline-flex; align-items: center; justify-content: center;
          background: linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%);
          color: #fff; margin-top: 2px;
        }
        .pricing-feature.is-excluded { color: rgba(255,255,255,0.32); }
        .pricing-feature.is-excluded .pricing-feature-icon {
          background: rgba(255,255,255,0.05); color: rgba(255,255,255,0.3);
        }

        /* ─── TAG DE DESCONTO (topo do card trimestral) ─── */
        .pricing-discount-tag {
          position: absolute; top: -11px; left: 50%; transform: translateX(-50%);
          z-index: 3; padding: 4px 12px; border-radius: 999px;
          font-size: 11px; font-weight: 700; letter-spacing: 0.04em; color: #fff;
          background: linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%);
          white-space: nowrap;
          animation: dp-badge-pop-center 2.4s ease-in-out infinite;
        }

        /* ─── TOGGLE Mensal/Trimestral ─── */
        .pricing-toggle {
          display: inline-flex; align-items: center; gap: 12px;
        }
        .pricing-toggle-label {
          background: none; border: none; padding: 0; cursor: pointer;
          font-family: inherit; font-size: 15px; font-weight: 700;
          letter-spacing: -0.2px; color: var(--dp-text-tertiary, rgba(255,255,255,0.45));
          transition: color 220ms ease;
        }
        .pricing-toggle-label:hover { color: var(--dp-text-secondary, rgba(255,255,255,0.7)); }
        .pricing-toggle-label.is-active { color: var(--dp-text-primary, #fff); }

        .pricing-switch {
          position: relative; box-sizing: border-box;
          width: 52px; height: 30px; padding: 0; border-radius: 999px;
          cursor: pointer; flex-shrink: 0;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.1);
          transition: background 280ms cubic-bezier(0.16,1,0.3,1),
            border-color 280ms ease, box-shadow 280ms ease;
        }
        .pricing-switch.is-on {
          background: linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%);
          border-color: transparent;
          box-shadow: 0 2px 12px rgba(13,174,255,0.35);
        }
        .pricing-switch-knob {
          position: absolute; top: 50%; left: 3px;
          width: 24px; height: 24px; border-radius: 50%;
          background: #fff; transform: translateY(-50%);
          box-shadow: 0 2px 6px rgba(0,0,0,0.35);
          transition: transform 280ms cubic-bezier(0.16,1,0.3,1);
        }
        .pricing-switch.is-on .pricing-switch-knob {
          transform: translateY(-50%) translateX(22px);
        }

        .pricing-save-badge {
          display: inline-flex; align-items: center;
          padding: 4px 10px; border-radius: 999px;
          font-size: 11px; font-weight: 800; letter-spacing: 0.02em; color: #fff;
          background: linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%);
          white-space: nowrap; pointer-events: none;
          animation: dp-badge-pop 2.4s ease-in-out infinite;
        }
        @keyframes dp-badge-pop {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.07); }
        }
        @keyframes dp-badge-pop-center {
          0%, 100% { transform: translateX(-50%) scale(1); }
          50%      { transform: translateX(-50%) scale(1.07); }
        }
        @media (prefers-reduced-motion: reduce) {
          .pricing-save-badge, .pricing-discount-tag { animation: none; }
        }

        /* ─── RESPONSIVE ─── */
        @media (max-width: 1280px) {
          .pricing-grid { grid-template-columns: repeat(3, 1fr); max-width: 820px; }
          .pricing-card.is-popular { transform: none; }
          .pricing-card.is-popular:hover { transform: translateY(-2px); }
        }
        @media (max-width: 760px) {
          .pricing-grid { grid-template-columns: repeat(2, 1fr); max-width: 540px; }
        }
        @media (max-width: 460px) {
          .pricing-grid { grid-template-columns: 1fr; max-width: 380px; }
          .pricing-grid > .pricing-item-popular { order: -1; }
        }
      `}</style>
    </div>
  );
};

export default PlanCardsSection;
